import { computed, watch } from 'vue'
import { useFormations, requiresExteriorSpace } from '../formations/useFormations.js'
import { getFormationLieuOptions } from '../formations/useFormationLieu.js'

/**
 * Composable pour gérer les formations dans QuoteDetail
 * Limite: 80 lignes max
 */
export function useQuoteDetailFormations(config) {
  const { quote, isEditing, editedQuote, getValue } = config
  const {
    categoriesList,
    loadingFormations,
    loadFormationsData,
    createFormationComputed,
    createFormationHandlers
  } = useFormations()

  const getFormValue = (field) => getValue(field)

  const {
    availableFormations,
    selectedFormation,
    formationMessage: baseFormationMessage,
    availableVilles,
    availableSessions,
    selectedSession
  } = createFormationComputed(getFormValue)

  const requiresExteriorSpaceForFormation = computed(() => {
    const nom = getValue('formation_nom')
    return requiresExteriorSpace(nom)
  })

  const formationLieuOptions = computed(() =>
    getFormationLieuOptions(selectedFormation.value, getValue('client_type'))
  )

  const formationMessage = computed(() => {
    if (!isEditing.value) return null
    return baseFormationMessage.value
  })

  // Les demandes créées avant l'ajout de formation_catalogue_id n'en portent pas : à
  // l'ouverture de l'édition, on hydrate la liste déroulante (indexée sur l'id) avec la
  // formation résolue par son nom, sinon elle s'afficherait vide.
  // Uniquement si ce nom ne désigne qu'une seule formation : sur un homonyme, deviner
  // reviendrait à enregistrer une durée arbitraire au premier enregistrement, alors que
  // le serveur refuse justement d'arbitrer (resolveFormationCatalogueId renvoie null).
  // La liste reste alors vide pour forcer un choix explicite.
  // Pas d'`immediate` : l'entrée en édition est déjà un changement de `isEditing`, et
  // évaluer la source dès le setup lirait la demande avant son chargement.
  watch(
    [isEditing, availableFormations],
    ([editing, formations]) => {
      if (!editing || editedQuote.value?.formation_catalogue_id) return
      const nom = editedQuote.value?.formation_nom
      if (!nom) return
      const matches = formations.filter(formation => formation.NOM === nom)
      if (matches.length === 1) {
        editedQuote.value.formation_catalogue_id = String(matches[0].ID)
      }
    }
  )

  const {
    onCategoryChange: baseOnCategoryChange,
    onFormationChange: baseOnFormationChange,
    onLieuChange: baseOnLieuChange,
    onVilleChange: baseOnVilleChange
  } = createFormationHandlers(
    editedQuote,
    getFormValue,
    () => formationMessage.value,
    () => selectedFormation.value
  )

  const onCategoryChange = () => baseOnCategoryChange()

  const onFormationChange = () => {
    baseOnFormationChange()
    if (formationMessage.value) {
      editedQuote.value.formation_message = formationMessage.value
    }
  }

  const onLieuChange = () => baseOnLieuChange()
  const onVilleChange = () => baseOnVilleChange()

  return {
    categories: categoriesList,
    loadingFormations,
    loadFormationsData,
    availableFormations,
    selectedFormation,
    formationMessage,
    availableVilles,
    availableSessions,
    selectedSession,
    requiresExteriorSpaceForFormation,
    formationLieuOptions,
    onCategoryChange,
    onFormationChange,
    onLieuChange,
    onVilleChange
  }
}
